import {
  CreateQuoteDocument,
  QuoteDocumentConfigPositionEnum,
} from '@monetize/types/app';
import { Transition } from 'history';
import { useCallback, useEffect, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { v4 as uuidv4 } from 'uuid';
import { handleApiErrorToast, parseMessageFromError } from '../api/axios';
import { useConfirmModal } from '../services/confirmModal';
import {
  QuoteDocumentUpload,
  quoteDocumentUploadsForOwnerSelector,
} from '../store/quoteDocumentUpload.store';
import {
  CONFIRM_LEAVE_PENDING_UPLOADS_DESC,
  CONFIRM_TAB_SWITCH_TITLE,
} from '../utils/messages';
import { useBlocker } from './usePrompt';

export interface NewQuoteDocumentUpload {
  file: File;
  position: QuoteDocumentConfigPositionEnum;
}

export type CreateQuoteDocumentFn = (
  data: CreateQuoteDocument,
  file: File,
) => Promise<void>;

export const useQuoteDocumentUploadsSummary = (ownerId: string) => {
  const uploads = useAtomValue(quoteDocumentUploadsForOwnerSelector(ownerId));
  const uploadingCount = useMemo(
    () => uploads.filter((upload) => upload.status === 'uploading').length,
    [uploads],
  );
  return { uploads, uploadingCount };
};

/**
 * Warns before in-app navigation away from the current page while this
 * owner (a quote, legal entity, or the tenant) has an upload that hasn't
 * finished saving. Scoped per-owner and called from the page that owns that
 * upload — not mounted app-wide — so it only guards navigation away from a
 * page that actually has something pending, rather than blocking every
 * route change in the app for an upload started somewhere else entirely.
 * Mirrors QuoteDrawer's handleClose pattern (useConfirmModal +
 * showConfirmModalPromise) rather than the plain window.confirm usePrompt
 * normally uses, since this warrants the app's own styled dialog.
 */
export const useQuoteDocumentUploadNavigationGuard = (ownerId: string) => {
  const { uploads } = useQuoteDocumentUploadsSummary(ownerId);
  const hasPendingUpload = uploads.length > 0;
  const { showConfirmModalPromise } = useConfirmModal();

  // useBlocker's blocker type is (tx: Transition) => void and doesn't await
  // its result, so this must stay synchronous — the confirm-modal promise
  // resolves later and retries the transition itself instead of being
  // returned.
  const blocker = useCallback(
    (tx: Transition) => {
      showConfirmModalPromise({
        title: CONFIRM_TAB_SWITCH_TITLE,
        description: CONFIRM_LEAVE_PENDING_UPLOADS_DESC,
        yesButton: 'Leave',
        yesBtnProps: { variant: 'primary' },
        noBtnProps: { variant: 'cancel' },
      }).then((confirmed) => {
        if (confirmed) {
          tx.retry();
        }
      });
    },
    [showConfirmModalPromise],
  );

  useBlocker(blocker, hasPendingUpload);
};

/**
 * Warns before the tab closes/refreshes while this owner (a quote, legal
 * entity, or the tenant) has an upload that hasn't finished saving. Scoped
 * per-owner and called from the page that owns that upload, matching
 * useQuoteDocumentUploadNavigationGuard — a real tab close/refresh does
 * wipe this owner's pending-upload state for good (unlike in-app
 * navigation, which the atom survives), so this only protects uploads
 * belonging to whichever page is currently open; an upload abandoned on a
 * page the user has since navigated away from won't trigger this warning.
 */
export const useQuoteDocumentUploadTabCloseGuard = (ownerId: string) => {
  const { uploads } = useQuoteDocumentUploadsSummary(ownerId);
  const hasPendingUpload = uploads.length > 0;

  useEffect(() => {
    if (!hasPendingUpload) {
      return undefined;
    }

    // Modern browsers ignore any custom message and show their own generic
    // prompt — setting returnValue is still required to trigger it.
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasPendingUpload]);
};

/**
 * Owns the pending-upload state machine for a quote's (or legal entity's)
 * documents. Uploads are tracked in a Jotai atom (not component state) so
 * they keep showing as uploading/failed no matter what unmounts while the
 * request is in flight — switching Internal/External view, or leaving the
 * page and coming back. `createDocument` still does the actual work (API
 * call + cache refresh); this hook only manages the visible status around
 * it, so the upload itself already keeps running in the background exactly
 * like it does today (see enqueueForQuote in api/quoteWriteChain.ts) — the
 * setter returned by useAtom below is tied to Provider, not to
 * whichever component instance called this hook, so updating status after
 * that component unmounts is safe.
 */
export const useQuoteDocumentUploads = (
  ownerId: string,
  createDocument: CreateQuoteDocumentFn,
) => {
  const [uploads, setUploads] = useAtom(
    quoteDocumentUploadsForOwnerSelector(ownerId),
  );

  const addFiles = useCallback(
    (files: NewQuoteDocumentUpload[]) => {
      if (files.length === 0) {
        return;
      }
      const newUploads: QuoteDocumentUpload[] = files.map(
        ({ file, position }) => ({
          localId: uuidv4(),
          ownerId,
          file,
          position,
          status: 'selecting-type',
          documentTypeId: null,
          errorMessage: null,
        }),
      );
      setUploads((prev) => [...prev, ...newUploads]);
    },
    [ownerId, setUploads],
  );

  const runUpload = useCallback(
    async (
      upload: QuoteDocumentUpload,
      documentTypeId: string,
      sortWithinPosition: number,
    ) => {
      setUploads((prev) =>
        prev.map((item) =>
          item.localId === upload.localId
            ? {
                ...item,
                status: 'uploading',
                documentTypeId,
                errorMessage: null,
              }
            : item,
        ),
      );
      try {
        await createDocument(
          {
            documentTypeId,
            position: upload.position,
            sortWithinPosition,
            autoAddToQuotes: false,
          },
          upload.file,
        );
        setUploads((prev) =>
          prev.filter((item) => item.localId !== upload.localId),
        );
      } catch (err) {
        handleApiErrorToast(err);
        const { summary, detail } = parseMessageFromError(err);
        setUploads((prev) =>
          prev.map((item) =>
            item.localId === upload.localId
              ? {
                  ...item,
                  status: 'error',
                  errorMessage:
                    detail || summary || 'Upload failed. Please try again.',
                }
              : item,
          ),
        );
      }
    },
    [createDocument, setUploads],
  );

  const retry = useCallback(
    (upload: QuoteDocumentUpload, sortWithinPosition: number) => {
      if (!upload.documentTypeId) {
        return Promise.resolve();
      }
      return runUpload(upload, upload.documentTypeId, sortWithinPosition);
    },
    [runUpload],
  );

  const remove = useCallback(
    (localId: string) => {
      setUploads((prev) => prev.filter((item) => item.localId !== localId));
    },
    [setUploads],
  );

  return { uploads, addFiles, selectType: runUpload, retry, remove };
};
